import { createClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export const api = {
  auth: {
    login: async (email, password) => {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;
      return { data: { token: data.session.access_token, user: data.user } };
    },
    register: async (email, password, metadata = {}) => {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            is_admin: false,
            ...metadata
          }
        }
      });
      if (error) throw error;
      return { data };
    },
    getMe: async () => {
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error) throw error;
      return { data: user };
    },
  },
  
  news: {
    getAll: async (publishedOnly = true) => {
      let query = supabase.from('news').select('*').order('created_at', { ascending: false });
      if (publishedOnly) {
        query = query.eq('published', true);
      }
      const { data, error } = await query;
      if (error) throw error;
      return { data };
    },
    getById: async (id) => {
      const { data, error } = await supabase.from('news').select('*').eq('id', id).single();
      if (error) throw error;
      return { data };
    },
    create: async (newsData) => {
      const { data, error } = await supabase.from('news').insert([newsData]).select();
      if (error) throw error;
      return { data: data[0] };
    },
    update: async (id, newsData) => {
      const { data, error } = await supabase.from('news').update(newsData).eq('id', id).select();
      if (error) throw error;
      return { data: data[0] };
    },
    delete: async (id) => {
      const { error } = await supabase.from('news').delete().eq('id', id);
      if (error) throw error;
      return { data: { success: true } };
    },
  },
  
  apps: {
    getAll: async (category = null, featuredOnly = false) => {
      let query = supabase.from('apps').select('*').order('created_at', { ascending: false });
      if (featuredOnly) {
        query = query.eq('featured', true);
      }
      if (category && category !== 'All') {
        query = query.eq('category', category);
      }
      const { data, error } = await query;
      if (error) throw error;
      return { data };
    },
    getById: async (id) => {
      const { data, error } = await supabase.from('apps').select('*').eq('id', id).single();
      if (error) throw error;
      return { data };
    },
    create: async (appData) => {
      const { data, error } = await supabase.from('apps').insert([appData]).select();
      if (error) throw error;
      return { data: data[0] };
    },
    update: async (id, appData) => {
      const { data, error } = await supabase.from('apps').update(appData).eq('id', id).select();
      if (error) throw error;
      return { data: data[0] };
    },
    delete: async (id) => {
      const { error } = await supabase.from('apps').delete().eq('id', id);
      if (error) throw error;
      return { data: { success: true } };
    },
  },
  
  plans: {
    getAll: async () => {
      const { data, error } = await supabase.from('plans').select('*').order('order', { ascending: true });
      if (error) throw error;
      return { data };
    },
    getById: async (id) => {
      const { data, error } = await supabase.from('plans').select('*').eq('id', id).single();
      if (error) throw error;
      return { data };
    },
    update: async (id, planData) => {
      const { data, error } = await supabase.from('plans').update(planData).eq('id', id).select();
      if (error) throw error;
      return { data: data[0] };
    },
  },
  
  analytics: {
    trackEvent: async (eventName, pagePath = null, eventData = {}) => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        await supabase.from('analytics_events').insert([{
          event_name: eventName,
          page_path: pagePath || window.location.pathname,
          user_id: user?.id || null,
          event_data: eventData,
        }]);
      } catch (err) {
        // Fallback silencioso para no ensuciar la consola en desarrollo
      }
    },
    getStats: async (days = 7) => {
      const dateLimit = new Date();
      dateLimit.setDate(dateLimit.getDate() - days);
      
      const { data, error } = await supabase
        .from('analytics_events')
        .select('*')
        .gte('created_at', dateLimit.toISOString());
        
      if (error) throw error;
      return { data };
    },
  },
  
  legal: {
    getAll: async () => {
      const { data, error } = await supabase.from('legal').select('*');
      if (error) throw error;
      return { data };
    },
    getById: async (id) => {
      const { data, error } = await supabase.from('legal').select('*').eq('id', id).single();
      if (error) throw error;
      return { data };
    },
    update: async (id, content) => {
      const { data, error } = await supabase.from('legal').update({ content }).eq('id', id).select();
      if (error) throw error;
      return { data: data[0] };
    },
  },
  
  services: {
    getAll: async () => {
      const { data, error } = await supabase.from('services').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return { data };
    },
    create: async (service) => {
      const { data, error } = await supabase.from('services').insert([service]).select();
      if (error) throw error;
      return { data: data[0] };
    },
    update: async (id, service) => {
      const { data, error } = await supabase.from('services').update(service).eq('id', id).select();
      if (error) throw error;
      return { data: data[0] };
    },
    delete: async (id) => {
      const { error } = await supabase.from('services').delete().eq('id', id);
      if (error) throw error;
    }
  },

  orders: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('orders')
        .select('*, services(*)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data };
    },
    getByUserId: async (userId) => {
      const { data, error } = await supabase
        .from('orders')
        .select('*, services(*)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return { data };
    },
    create: async (orderData) => {
      const { data, error } = await supabase.from('orders').insert([orderData]).select();
      if (error) throw error;
      return { data: data[0] };
    },
    update: async (id, orderData) => {
      const { data, error } = await supabase.from('orders').update(orderData).eq('id', id).select();
      if (error) throw error;
      return { data: data[0] };
    },
    uploadReceipt: async (orderId, file, type = 'advance') => {
      const fileExt = file.name.split('.').pop();
      const fileName = `${orderId}-${type}-${Math.random()}.${fileExt}`;
      const filePath = `receipts/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('service-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('service-assets')
        .getPublicUrl(filePath);

      const updateData = type === 'advance' 
        ? { advance_receipt_url: publicUrl } 
        : { final_receipt_url: publicUrl };

      const { data, error } = await supabase.from('orders').update(updateData).eq('id', orderId).select();
      if (error) throw error;
      return { data: data[0] };
    }
  }
};

export const isAuthenticated = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

export const logout = async () => {
  await supabase.auth.signOut();
};
